const AVATAR_COLORS = ["danger", "success", "warning", "accent", "default"];

const getInitials = (fullname = "") => {
  const parts = fullname.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Deterministic color per person so the same member always gets the same color
// across renders/sessions, instead of random reshuffling.
const getColorForEmail = (email = "") => {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const normalizeMeet = (meet, { maxVisible = 2 } = {}) => {
  const members = (meet.members || []).map((m) => ({
    email: m.member_email,
    fullname: m.member_fullname,
    initials: getInitials(m.member_fullname),
    color: getColorForEmail(m.member_email),
    id: m.member_id || m._id
  }));

  return {
    id: meet._id,
    title: meet.title?.trim() || "Untitled Meeting",
    subtitle: meet.description?.trim() || "",
    meets_date: meet.meeting_date,
    start_time: meet.start_time?.set || "",
    end_time: meet.end_time?.set || "",
    shortCode: meet.short_code,
    members,
    visibleMembers: members.slice(0, maxVisible),
    extraCount: Math.max(0, members.length - maxVisible),
  };
};

export const normalizeMeets = (meets, opts) => meets.map((m) => normalizeMeet(m, opts));

export const groupMeetsByDate = (meets) => {
  return meets.reduce((acc, meet) => {
    const key = meet.meeting_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(meet);
    return acc;
  }, {});
};

export const toConnectionShape = (members) =>
  members?.map((m) => ({ id: m.member_id, fullname: m.member_fullname, email_address: m.member_email }));

export const toMeetingMemberShape = (participants) =>
  participants?.map((p) => ({ member_id: p.id, member_fullname: p.fullname, member_email: p.email_address }));

/** Builds a Google Maps URL from a location object, preferring place_id over lat/lng over name. */
export function buildMapsUrl(location) {
  if (!location) return null;
  if (location.place_id) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      location.name || "",
    )}&query_place_id=${encodeURIComponent(location.place_id)}`;
  }
  if (location.lat != null && location.lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
  }
  const q = [location.name, location.description].filter(Boolean).join(" ");
  if (!q) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export function buildLocationPayload(location) {
  if (!location) return { name: "", place_id: "", lat: "", lng: "" };
  return {
    name: location.name ?? "",
    place_id: location.place_id ?? "",
    lat: location.lat ?? "",
    lng: location.lng ?? "",
  };
}